import { Component, effect, EventEmitter, Injector, Input, OnInit, Output, runInInjectionContext, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { FilterSelectItem } from '../../../models/filter-select-item';
import { PreferredActionType } from '../../../models/preferred-action-type';
import { FieldFilterService } from '../../../services/filters/field-filter.service';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'app-preferred-action-select',
    templateUrl: './preferred-action-select.component.html',
    styleUrls: ['./preferred-action-select.component.scss'],
    imports: [MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, MatTooltip, MatSelectTrigger, MatOption, MatError]
})
export class PreferredActionSelectComponent implements OnInit {

  private _disabled = signal(false);

  @Input() set disabled(value: boolean) {
    this._disabled.set(value);
  }


  @Input() form: FormGroup | null = null;
  @Input() color = 'primary';
  @Input() appearance = 'outline';
  @Input() floatLabel = 'always';
  @Input() multiple = true;
  @Input() controlName = 'preferred-action';
  @Input() label = "Preferred Action";
  @Input() placeholder = "";
  @Input() toolTipPosition = 'above';
  @Input() selectAllEnabled = true;
  @Input() noneEnabled = false;
  @Input() required = false;
  @Input() typeIds = [PreferredActionType.Opportunity.valueOf()];
  @Input() onlyShowOptions: string[] = [];

  options: FilterSelectItem[] = [];
  allOption: FilterSelectItem = {ID: 0, Description: 'All', LinkAssociations: [], Selected: false};
  noneOption: FilterSelectItem = {ID: -1, Description: 'None', LinkAssociations: [], Selected: false};
  control = new FormControl(null);
  private pendingSingleValue: FilterSelectItem | null = null;

  @Output() ready = new EventEmitter();
  @Output() valueChanged = new EventEmitter();

  @ViewChild(MatSelect) select: MatSelect;

  constructor(private filterService: FieldFilterService, private injector: Injector) { 
  }

  ngOnInit(): void {

    if(this.required) {
      this.control.setValidators([Validators.required]);
    }

    this.form?.addControl(this.controlName, this.control);

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if(this._disabled()) {
          this.control?.disable({emitEvent: false});
        } else {
          this.control?.enable({emitEvent: false});
        }
      })});

    this.getOptionData();
  }

  getOptionData() {

    this.filterService.getPreferredActionOptions(this.typeIds.toString()).subscribe(result => {
      const items = result?.sort((a, b) => {
        return a.Description.toLowerCase().localeCompare(b.Description.toLowerCase());
      });

      this.options = this.onlyShowOptions.length > 0 ? items.filter(i => this.onlyShowOptions.includes(i.Description)) : items;

      this.applyPendingSingleValue();
      this.ready.emit(this.options);
    });

  }

  setValues(values: FilterSelectItem[]) {
    if(!values) {
      return;
    }

    if(values.length == 0) {
      this.control.setValue([this.allOption]);
      this.onToggleItem(this.select.options.get(0));
      return;
    }

    this.allOption.Selected = false;
    this.control.setValue(values);
  }

  setSingleModeValue(value: FilterSelectItem) {
    if(!value) {
      return;
    }

    if(!this.options || this.options.length === 0) {
      this.pendingSingleValue = value;
      return;
    }

    const item = this.options.find(i => i.ID == value.ID);

    this.control.setValue(item);
  }

  private applyPendingSingleValue() {
    if(!this.pendingSingleValue) {
      return;
    }

    const pending = this.pendingSingleValue;
    this.pendingSingleValue = null;
    this.setSingleModeValue(pending);
  }

  getValues() {
    if(this.allOption.Selected) {
      return [this.allOption];
    }

    return this.control?.value;
  }

  onToggleItem(item: MatOption) {
    if(!this.multiple) {
      return;
    }

    if(item.value == this.allOption) {
      if(item.selected) {
        this.allOption.Selected = true;
        this.select.options.forEach(o => o.select());
        return;
      }

      this.allOption.Selected = false;
      this.select.options.forEach(o => o.deselect());

    } else {
      
      const allItem = this.select.options.get(0);

      if(!item.selected && allItem.selected) {
        this.allOption.Selected = false;
        allItem.deselect();
        return;
      }

      if(item.selected && !allItem.selected) {
        const check = this.checkOptionAllStatus();
        if(check) {
          this.allOption.Selected = true;
          allItem.select();
        }
      }
    }
  }

  onSelectionChange() {
    this.valueChanged.emit(this.control?.value);
  }

  checkOptionAllStatus() {
    let result = true;
    this.select.options.forEach(o => {
      if(o.value != this.allOption && !o.selected) {
        result = false;
      }
    });

    return result;
  }

  getTooltipString() {
    let result = '';
    const options = this.allOption.Selected ? this.control?.value.slice(1) : this.control?.value;

    options.forEach((o, index) => {
      result += o.Description;

      if(index < options.length - 1) {
        result += ', ';
      }
    })
    return result;
  }

  getSelectCount() {
    return (this.control.value?.length <= this.options.length) 
      ? this.control.value?.length 
      : this.control.value?.length - 1;
  }

}
